import { trabaApi } from '@traba/api-utils'
import { useAlert } from '@traba/context'
import {
  captureSentryError,
  getErrorMessage,
  persistLastAuthMethodAfterSuccessfulLogin,
  AuthAppId,
} from '@traba/utils'
import { User, getAuth, onAuthStateChanged } from 'firebase/auth'
import React from 'react'
import { useUserContext } from 'src/context/user/UserContext'
import { UserProfile } from 'src/types'

type Company = {
  employerName?: string
  companyId?: string
  category?: string
  createdAt?: Date
}

export function AuthProvider({ children }: { children: JSX.Element }) {
  const userContext = useUserContext()
  const { handleError } = useAlert()
  const auth = getAuth()

  // Meticulous dev replay: skip the live token refresh + sign-out so a recorded session renders (backend Bearer auth still gates all data).
  const isMeticulousReplay =
    import.meta.env.VITE_ENV === 'development' &&
    window.Meticulous?.isRunningAsTest === true

  async function fetchUserAndUpdateContext() {
    try {
      // /me and /my-company are independent — fetch them together instead of
      // serially. /me is required; /my-company is best-effort (404 / failure
      // tolerated, same as before).
      const [meResult, companyResult] = await Promise.allSettled([
        trabaApi.get('/me'),
        trabaApi.get('/my-company'),
      ])

      if (meResult.status === 'rejected') {
        throw meResult.reason
      }
      const trabaApiUser = meResult.value.data as UserProfile

      let company: Company | undefined
      if (companyResult.status === 'fulfilled') {
        company = companyResult.value.data as Company
      } else {
        captureSentryError(companyResult.reason)
      }

      if (!trabaApiUser.uid) {
        throw new Error('UserLoginError')
      }

      if (window.analytics) {
        const { onboarding: _o, createdAt, ...traits } = trabaApiUser
        window.analytics.identify(trabaApiUser.uid, {
          ...traits,
          phone: traits.phoneNumber,
          created: createdAt?.toISOString?.(),
          company: {
            name: company?.employerName,
            id: company?.companyId,
            industry: company?.category,
            created: company?.createdAt?.toISOString?.(),
          },
        })
      }

      userContext.dispatch({ type: 'USER_LOGIN', value: trabaApiUser })
    } catch (error: unknown) {
      handleError(
        error,
        'AuthProvider -> fetchUserAndUpdateContext()',
        'Please try again.',
        'Failed to complete login',
        10000,
      )
      throw error
    }
  }

  async function upsertUser(user: User) {
    try {
      const fullNameArray = user.displayName?.split(' ')
      const firstName = fullNameArray ? fullNameArray[0] : ''
      const lastName = fullNameArray ? fullNameArray[1] : ''
      await trabaApi.post('/users/upsert', {
        email: user.email,
        firstName,
        lastName,
        uid: user.uid,
        photoUrl: user.photoURL,
        phoneNumber: user.phoneNumber,
      })
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error) ?? ''
      if (errorMessage === 'invitation/already-accepted') {
        return
      }
      handleError(
        error,
        'AuthProvider -> upsertUser()',
        'Please contact support.',
        'There was an error during login',
        10000,
      )
      throw error
    }
  }

  async function completeTokenExchange() {
    if (isMeticulousReplay) {
      return
    }
    try {
      await trabaApi.post('/users/auth-token')
      await auth.currentUser?.getIdToken(true)
    } catch (error: unknown) {
      handleError(
        error,
        'AuthProvider -> completeTokenExchange()',
        'Please try again.',
        'Failed to complete login',
        10000,
      )
      throw error
    }
  }

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        userContext.dispatch({ type: 'SET_IS_LOADING', value: true })
        try {
          await upsertUser(user)
          if (window.analytics) {
            window.analytics.identify(user.uid, {
              userId: user.uid,
              email: user.email,
            })
            window.analytics.track('User Logged In', {
              userId: user.uid,
              email: user.email,
            })
          }

          await completeTokenExchange()
          await fetchUserAndUpdateContext()
          await persistLastAuthMethodAfterSuccessfulLogin(user, AuthAppId.Neo)
        } catch {
          userContext.dispatch({ type: 'USER_LOGOUT' })
          // Under Meticulous replay, skip the sign-out (it triggers the login redirect that breaks replay); USER_LOGOUT above already clears the loading state.
          if (isMeticulousReplay) {
            return
          }
          auth.signOut()
        }
      } else {
        userContext.dispatch({ type: 'USER_LOGOUT' })
      }
    })
    return unsubscribe
  }, [])

  return children
}
